import { ChainId } from '@donaswap/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BottomDrawer, CurrencyLogo, Flex, IconButton, Text, TooltipText, useMatchBreakpoints, useTooltip } from '@donaswap/uikit'
import styled from 'styled-components'
import { isMobile } from 'react-device-detect'
import { useAtom } from 'jotai'
import atomWithStorageWithErrorCatch from 'utils/atomWithStorageWithErrorCatch'
import { useTranslation } from '@donaswap/localization'
import { StyledPriceChart } from 'views/Swap/components/Chart/styles'
import { useTheme } from '@donaswap/hooks'
import { Field } from 'state/swap/actions'
import { useSwapState } from 'state/swap/hooks'
import { useActiveChainId } from "./useActiveChainId"
import { useCurrency } from './Tokens'
import { MoonscanIcon2 } from '@donaswap/uikit'

// function MoonscanIcon({ color, bgColor = "transparent", width, crossed = false }) {
//   return (
//     <svg width={width} height={width} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 149.25 149.25">
//       <defs>
//         <style>{`.cls-1{fill:${color};}`}</style>
//       </defs>
//       <circle className="cls-1" cx="74.62" cy="74.62" r="37.31"/>
//       <path className="cls-1" d="M74.62,149.25a74.63,74.63,0,1,1,74.63-74.63A74.71,74.71,0,0,1,74.62,149.25Zm0-145.53a70.9,70.9,0,1,0,70.9,70.9A71,71,0,0,0,74.62,3.72Z"/>
//       <circle className="cls-1" cx="126.01" cy="23.24" r="18.66"/>
//       {crossed && (
//         <>
//           <line x1="0" y1="149.25" x2="149.25" y2="0" strokeWidth="24px" stroke={bgColor} />
//           <line x1="0" y1="149.25" x2="149.25" y2="0" strokeWidth="12px" stroke={color} />
//         </>
//       )}
//     </svg>
//   )
// }

export type Moonscan = {
  moonscanButton: JSX.Element,
  isMoonscanSupported: boolean,
  moonscanChart: JSX.Element,
}

const MOONSCAN_CHAIN_MAP = {
  [ChainId.BSC]: 'bsc',
  [ChainId.ETHEREUM]: 'eth',
}

const SUPPORTED_CHAIN_IDS = Object.keys(MOONSCAN_CHAIN_MAP)

const ColoredIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.textSubtle};
  overflow: hidden;
`

const mobileShowOnceTokenHighlightAtom = atomWithStorageWithErrorCatch('pcs::mobileShowOnceTokenHighlightV2', true)

export default function useMoonscan(): Moonscan {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()
  const [mobileTooltipShowOnce, setMobileTooltipShowOnce] = useAtom(mobileShowOnceTokenHighlightAtom)
  const [mobileTooltipShow, setMobileTooltipShow] = useState(false)
  const { tooltip, tooltipVisible, targetRef } = useTooltip(<Text>{t('Yedi for this token')}</Text>, {
    placement: isMobile ? 'top' : 'bottom',
    trigger: isMobile ? 'focus' : 'hover',
    ...(isMobile && { manualVisible: mobileTooltipShow }),
  })
  const [ isChartVisible, setIsChartVisible ] = useState(false)
  const isMoonscanSupported = useMemo(() => SUPPORTED_CHAIN_IDS.includes(chainId.toString()), [chainId])
  const { isDesktop } = useMatchBreakpoints()
  const { isDark } = useTheme()

  // swap state & price data
  const {
    // [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()
  // const inputCurrency = useCurrency(inputCurrencyId)
  const outputCurrency = useCurrency(outputCurrencyId)
  // const token0Address = inputCurrency?.wrapped.address?.toLocaleLowerCase()
  const token1Address = outputCurrency?.wrapped.address?.toLocaleLowerCase()

  const mobileTooltipClickOutside = useCallback(() => {
    setMobileTooltipShow(false)
  }, [])

  const toggleIsChartVisible = useCallback(() => {
    setIsChartVisible(v => !v)
  }, [])

  useEffect(() => {
    if (isMobile && !mobileTooltipShowOnce) {
      setMobileTooltipShow(true)
      setMobileTooltipShowOnce(true)
    }
  }, [mobileTooltipShowOnce, setMobileTooltipShowOnce])

  useEffect(() => {
    document.body.addEventListener('click', mobileTooltipClickOutside)
    return () => {
      document.body.removeEventListener('click', mobileTooltipClickOutside)
    }
  }, [mobileTooltipClickOutside])

  // button
  const moonscanButton = useMemo(() => {
    return isMoonscanSupported && (
      <ColoredIconButton
        variant="text"
        scale="sm"
        onClick={toggleIsChartVisible}
      >
        {isChartVisible ? (
          <MoonscanIcon2 width="22px" marginTop={2} />
        ) : (
          <>
            <TooltipText
              ref={targetRef}
              onClick={() => setMobileTooltipShow(false)}
              display="flex"
              style={{ justifyContent: 'center' }}
            >
              <MoonscanIcon2 width="22px" marginTop={2} />
            </TooltipText>
            {tooltipVisible && (!isMobile || mobileTooltipShow) && tooltip}
          </>
        )}
      </ColoredIconButton>
    )
  }, [isDark, targetRef, toggleIsChartVisible, isChartVisible, isMoonscanSupported, tooltip, tooltipVisible, isMobile, mobileTooltipShow])

  const renderChart = useCallback(() => {
    return (
      <StyledPriceChart
        style={{ height: "calc(100% - 64px)", borderRadius: "24px" }}
        overflow="unset"
        $isDark={isDark}
        $isExpanded
        $isFullWidthContainer={isMobile}
      >
        <Flex justifyContent="space-between" px="24px">
          <Flex alignItems="center">
            {outputCurrency && <CurrencyLogo currency={outputCurrency} size="24px" style={{ marginRight: '8px' }} />}
            {outputCurrency && (
              <Text color="text" bold>
                {outputCurrency.name} ({outputCurrency.symbol})
              </Text>
            )}
          </Flex>
          {!isMobile && (
            <Flex height="64px" />
          )}
        </Flex>
        {token1Address && outputCurrency ? (
          <iframe title={`moonscan graph for ${token1Address} on ${MOONSCAN_CHAIN_MAP[outputCurrency.chainId]?.toUpperCase?.()}`} src={`https://embed.moonscan.com/token/${token1Address}:${MOONSCAN_CHAIN_MAP[outputCurrency.chainId]}`} width="100%" height="90%" />
        ) : null}
      </StyledPriceChart>
    )
  }, [isDark, outputCurrency, token1Address])

  const moonscanChart = useMemo(() => {
    if (!isChartVisible) {
      return null
    }

    if (isDesktop && isMoonscanSupported) {
      return renderChart()
    }
    
    if (!isDesktop && isMoonscanSupported) {
      return (
        <BottomDrawer 
          content={renderChart()}
          isOpen={isChartVisible}
          setIsOpen={setIsChartVisible}
        />
      )
    }

    return null
  }, [isDesktop, isMoonscanSupported, isChartVisible, renderChart])

  return {
    moonscanButton,
    isMoonscanSupported,
    moonscanChart,
  }
}